import React from 'react';
import { mockData } from '../data/mock';

const SITE = 'https://www.bouhdel.com';

const DAYS = {
  weekdays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  saturday: 'Saturday',
  sunday: 'Sunday',
};

// "9h-13h | 14h-18h" -> deux OpeningHoursSpecification ; "Fermé" -> aucune
const toTime = (s) => {
  const [h, m] = s.trim().split('h');
  return `${h.padStart(2, '0')}:${(m || '00').padEnd(2, '0')}`;
};

const openingHours = (hours = {}) =>
  Object.entries(hours).flatMap(([key, value]) =>
    !DAYS[key] || /fermé/i.test(value)
      ? []
      : value.split('|').map((range) => {
          const [opens, closes] = range.split('-').map(toTime);
          return { '@type': 'OpeningHoursSpecification', dayOfWeek: DAYS[key], opens, closes };
        })
  );

const slug = (s) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-');

const agencyId = (agency) => `${SITE}/#${slug(agency.name)}`;

const postalAddress = (agency) => ({
  '@type': 'PostalAddress',
  streetAddress: agency.address,
  addressLocality: agency.city,
  postalCode: agency.zipCode,
  addressCountry: 'FR',
});

// JSON.stringify n'échappe pas "</script>" : on neutralise les "<".
const ld = (data) => ({ __html: JSON.stringify(data).replace(/</g, '\\u003c') });

/**
 * Composant pour données structurées JSON-LD (Schema.org)
 * Aide Google à comprendre le type d'entreprise et améliore le SEO local
 */
const StructuredData = () => {
  // Organisation principale
  const organizationData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE}/#organization`,
    name: 'Bouhdel',
    url: SITE,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE}/logo.png`,
      width: '300',
      height: '100',
    },
    description: 'Constructeur de maisons individuelles sur mesure dans le sud de la France depuis plus de 20 ans.',
    foundingDate: '2003',
    address: postalAddress(mockData.agencies[0]),
    sameAs: [
      'https://www.facebook.com/p/Bouhdel-Constructeur-de-maisons-individuelles-100086627212377/',
      'https://www.instagram.com/bouhdel_/',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: mockData.company.phone,
      contactType: 'customer service',
      areaServed: 'FR',
      availableLanguage: ['French'],
    },
    // Le parcours client de mock.js, lisible par une machine.
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Accompagnement construction de maison individuelle',
      itemListElement: mockData.services.map((service) => ({
        '@type': 'Service',
        name: service.title,
        description: service.description,
        provider: { '@id': `${SITE}/#organization` },
      })),
    },
    // Réciproque du parentOrganization porté par chaque agence.
    subOrganization: mockData.agencies.map((agency) => ({ '@id': agencyId(agency) })),
  };

  // GeneralContractor pour chaque agence, généré depuis mock.js
  const locationsData = mockData.agencies.map((agency) => ({
    '@context': 'https://schema.org',
    '@type': 'GeneralContractor',
    '@id': agencyId(agency),
    name: `Bouhdel - ${agency.name}`,
    url: `${SITE}/#agences`,
    image: `${SITE}${agency.image}`,
    address: postalAddress(agency),
    geo: {
      '@type': 'GeoCoordinates',
      latitude: String(agency.coordinates.lat),
      longitude: String(agency.coordinates.lng),
    },
    telephone: agency.phone,
    email: agency.email,
    openingHoursSpecification: openingHours(agency.hours),
    areaServed: agency.coverageArea.map((town) => ({ '@type': 'City', name: town })),
    parentOrganization: { '@id': `${SITE}/#organization` },
  }));

  // WebSite schema
  const websiteData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE}/#website`,
    url: SITE,
    name: 'Bouhdel',
    description: 'Constructeur de maisons individuelles sur mesure dans le sud de la France',
    publisher: { '@id': `${SITE}/#organization` },
    inLanguage: 'fr-FR',
  };

  // BreadcrumbList pour navigation
  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Accueil',
        item: SITE,
      },
    ],
  };

  return (
    <>
      {/* Organization */}
      <script type="application/ld+json" dangerouslySetInnerHTML={ld(organizationData)} />

      {/* Website */}
      <script type="application/ld+json" dangerouslySetInnerHTML={ld(websiteData)} />

      {/* GeneralContractor pour chaque agence */}
      {locationsData.map((location) => (
        <script key={location['@id']} type="application/ld+json" dangerouslySetInnerHTML={ld(location)} />
      ))}

      {/* Breadcrumb */}
      <script type="application/ld+json" dangerouslySetInnerHTML={ld(breadcrumbData)} />
    </>
  );
};

export default StructuredData;
